
import React, { useState } from 'react';
import { LegalPayment, CaseFile } from '../types';

interface PaymentsViewProps {
  payments: LegalPayment[];
  cases: CaseFile[];
  onAddPayment: (p: LegalPayment) => void;
  onUpdatePayment: (id: string, updates: Partial<LegalPayment>) => void;
  onDeletePayment: (id: string) => void;
  onToggleStatus: (id: string) => void;
}

const PaymentsView: React.FC<PaymentsViewProps> = ({ payments, cases, onAddPayment, onUpdatePayment, onDeletePayment, onToggleStatus }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'incasare' | 'plata'>('all');
  
  const [newPay, setNewPay] = useState({
    title: '', amount: '', currency: 'RON', direction: 'incasare', type: 'onorariu', status: 'pending', recurring: 'none', dueDate: '', linkedCaseId: ''
  });

  const exportToCSV = () => {
    const filteredPayments = payments.filter(p => filter === 'all' || p.direction === filter);
    const headers = ["Data", "Titlu", "Dosar", "Suma", "Valuta", "Directie", "Status"];
    const rows = filteredPayments.map(p => {
      const caseRef = cases.find(c => c.id === p.linkedCaseId)?.caseNumber || "N/A";
      return [new Date(p.dueDate).toLocaleDateString('ro-RO'), `"${p.title.replace(/"/g, '""')}"`, caseRef, p.amount, p.currency, p.direction === 'incasare' ? 'Venit' : 'Cheltuiala', p.status];
    });
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Export_LexSmart.csv`);
    link.click();
  };

  const handleSave = () => {
    const numAmount = parseFloat(newPay.amount);
    if (!newPay.title || isNaN(numAmount) || numAmount <= 0) return;

    if (editingId) {
      onUpdatePayment(editingId, {
        title: newPay.title,
        amount: numAmount,
        currency: newPay.currency as any,
        dueDate: newPay.dueDate || new Date().toISOString(),
        direction: newPay.direction as any,
        linkedCaseId: newPay.linkedCaseId
      });
      setEditingId(null);
    } else {
      const pay: LegalPayment = {
        id: Math.random().toString(36).substr(2, 9),
        title: newPay.title, amount: numAmount, currency: newPay.currency as any,
        dueDate: newPay.dueDate || new Date().toISOString(),
        type: newPay.type as any, direction: newPay.direction as any, status: 'pending',
        recurring: newPay.recurring as any, linkedCaseId: newPay.linkedCaseId
      };
      onAddPayment(pay);
    }
    setIsAdding(false);
    setNewPay({ title: '', amount: '', currency: 'RON', direction: 'incasare', type: 'onorariu', status: 'pending', recurring: 'none', dueDate: '', linkedCaseId: '' });
  };

  const startEdit = (p: LegalPayment) => {
    setEditingId(p.id);
    setNewPay({
      title: p.title,
      amount: p.amount.toString(),
      currency: p.currency,
      direction: p.direction,
      type: p.type,
      status: p.status,
      recurring: p.recurring || 'none',
      dueDate: p.dueDate.split('T')[0],
      linkedCaseId: p.linkedCaseId || ''
    });
    setIsAdding(true);
    // Scroll to form on mobile
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const filteredPayments = payments.filter(p => filter === 'all' || p.direction === filter);
  const totalIncasat = payments.filter(p => p.direction === 'incasare' && p.status === 'completed').reduce((acc, curr) => acc + curr.amount, 0);
  const totalDeIncasat = payments.filter(p => p.direction === 'incasare' && p.status === 'pending').reduce((acc, curr) => acc + curr.amount, 0);
  const totalPlati = payments.filter(p => p.direction === 'plata' && p.status === 'pending').reduce((acc, curr) => acc + curr.amount, 0);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight italic">Finanțe Cabinet</h2>
          <p className="text-slate-400 font-bold uppercase text-[8px] md:text-[10px] tracking-widest mt-1 italic">Flux de numerar și onorarii</p>
        </div>
        <div className="flex flex-wrap gap-2 md:gap-3 w-full md:w-auto">
          <button onClick={exportToCSV} className="flex-grow md:flex-none px-4 md:px-6 py-3 md:py-4 bg-slate-900 text-white rounded-xl md:rounded-2xl font-black text-[8px] md:text-[10px] uppercase tracking-widest shadow-lg">📊 Export CSV</button>
          {!isAdding && (
            <button onClick={() => { setIsAdding(true); setEditingId(null); }} className="flex-grow md:flex-none bg-indigo-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl font-black text-[8px] md:text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-100">+ Adaugă Tranzacție</button>
          )}
        </div>
      </div>

      {isAdding && (
        <div className="bg-white p-6 md:p-12 rounded-[2rem] md:rounded-[3.5rem] shadow-2xl border border-indigo-100 animate-in slide-in-from-top-4">
          <div className="flex justify-between items-center mb-8">
            <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest italic">{editingId ? 'Editare Tranzacție' : 'Tranzacție Nouă'}</h4>
            {editingId && (
              <button onClick={() => { setIsAdding(false); setEditingId(null); }} className="text-indigo-600 font-black text-[10px] uppercase tracking-widest">X Anulează Editarea</button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Descriere</label>
              <input className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl outline-none text-xs font-bold italic" value={newPay.title} onChange={e => setNewPay({...newPay, title: e.target.value})} placeholder="Titlu (Ex: Onorariu Popescu)" />
            </div>
            <div className="space-y-2">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Suma (RON)</label>
              <input className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl outline-none text-xs font-bold" value={newPay.amount} onChange={e => setNewPay({...newPay, amount: e.target.value})} placeholder="Ex: 5000" />
            </div>
            <div className="space-y-2">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Tip Tranzacție</label>
              <select className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl outline-none text-xs font-black uppercase tracking-widest" value={newPay.direction} onChange={e => setNewPay({...newPay, direction: e.target.value as any})}>
                <option value="incasare">Încasare (Venit)</option>
                <option value="plata">Plată (Cheltuială)</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Data Scadență</label>
              <input type="date" className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl outline-none text-xs font-bold" value={newPay.dueDate} onChange={e => setNewPay({...newPay, dueDate: e.target.value})} />
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <button onClick={() => { setIsAdding(false); setEditingId(null); }} className="px-6 py-3 text-[9px] font-black text-slate-400 uppercase">Anulează</button>
            <button onClick={handleSave} className="px-10 py-4 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase shadow-lg shadow-indigo-100">{editingId ? 'Actualizează' : 'Salvează'}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
        {[
          { label: 'Încasat', val: totalIncasat, color: 'text-emerald-600', icon: '✅' },
          { label: 'De Încasat', val: totalDeIncasat, color: 'text-indigo-600', icon: '⏳' },
          { label: 'Datorii', val: totalPlati, color: 'text-rose-500', icon: '💳' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 shadow-xl relative overflow-hidden">
            <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">{stat.label}</p>
            <div className="flex items-baseline gap-2">
              <p className={`text-2xl md:text-4xl font-black tracking-tighter ${stat.color}`}>{stat.val.toLocaleString()}</p>
              <span className="text-slate-300 font-black text-[10px] md:text-sm uppercase italic">RON</span>
            </div>
            <span className="absolute top-4 right-4 text-xl opacity-20">{stat.icon}</span>
          </div>
        ))}
      </div>

      <div className="flex bg-slate-100 p-1 rounded-xl md:rounded-2xl border border-slate-200 w-full md:w-fit overflow-x-auto">
        {(['all', 'incasare', 'plata'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`flex-grow md:flex-none px-6 md:px-8 py-2 md:py-3 rounded-lg md:rounded-xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all ${filter === f ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>
            {f === 'all' ? 'Toate' : f === 'incasare' ? 'Venituri' : 'Cheltuieli'}
          </button>
        ))}
      </div>

      <div className="hidden md:block bg-white rounded-[2rem] md:rounded-[3rem] border border-slate-50 shadow-2xl overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-50">
              <th className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Tranzacție</th>
              <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Valoare</th>
              <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Data</th>
              <th className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredPayments.map(p => (
              <tr key={p.id} className="hover:bg-slate-50/50 transition-colors group">
                <td className="px-10 py-6">
                  <p className="font-black text-slate-900 italic tracking-tight">{p.title}</p>
                  <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Dosar: {cases.find(c => c.id === p.linkedCaseId)?.caseNumber || 'General'}</p>
                </td>
                <td className={`px-8 py-6 font-black ${p.direction === 'incasare' ? 'text-emerald-600' : 'text-rose-500'}`}>{p.direction === 'incasare' ? '+' : '-'} {p.amount.toLocaleString()} RON</td>
                <td className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase">{new Date(p.dueDate).toLocaleDateString('ro-RO')}</td>
                <td className="px-10 py-6 text-right space-x-2">
                  <button onClick={() => startEdit(p)} className="p-2 hover:bg-indigo-50 text-indigo-400 rounded-lg transition-all" title="Editează">✏️</button>
                  <button onClick={() => onDeletePayment(p.id)} className="p-2 hover:bg-rose-50 text-rose-400 rounded-lg transition-all" title="Șterge">🗑️</button>
                  <button onClick={() => onToggleStatus(p.id)} className={`px-5 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest ${p.status === 'completed' ? 'bg-slate-100 text-slate-400' : 'bg-indigo-600 text-white'}`}>{p.status === 'completed' ? 'Finalizat' : 'Confirmă'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-4">
        {filteredPayments.map(p => (
          <div key={p.id} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-md relative overflow-hidden">
            <div className="flex justify-between items-start mb-4">
              <div onClick={() => startEdit(p)} className="cursor-pointer">
                <p className="font-black text-slate-900 text-xs italic leading-tight">{p.title}</p>
                <p className="text-[8px] text-slate-400 font-bold uppercase mt-1">{new Date(p.dueDate).toLocaleDateString('ro-RO')}</p>
              </div>
              <div className="flex flex-col items-end">
                <p className={`font-black text-xs ${p.direction === 'incasare' ? 'text-emerald-600' : 'text-rose-500'}`}>{p.direction === 'incasare' ? '+' : '-'} {p.amount.toLocaleString()} RON</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => startEdit(p)} className="text-[10px] opacity-40">✏️</button>
                  <button onClick={() => onDeletePayment(p.id)} className="text-[10px] opacity-40">🗑️</button>
                </div>
              </div>
            </div>
            <button onClick={() => onToggleStatus(p.id)} className={`w-full py-3.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${p.status === 'completed' ? 'bg-slate-50 text-slate-300 italic' : 'bg-indigo-600 text-white shadow-xl shadow-indigo-100'}`}>
              {p.status === 'completed' ? 'Tranzacție Finalizată' : 'Confirmă Încasarea/Plata'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentsView;
